/**
 * EditEntryModal — Modal for editing a time entry.
 * Sprint 10, SDD §11.2, FR-EDIT-01
 */

import { useState, useEffect, useRef } from 'react';
import { CATEGORIES } from '@shared/categories';
import { POSITIONS } from '@shared/positions';
import { APPARATUS, isFlexUnit } from '@shared/apparatus';
import { apiFetch } from '../lib/api';
import { getRolePriority } from '@shared/roles';
import type { TimeEntry } from './TimeEntryList';

interface EditEntryModalProps {
  entry: TimeEntry;
  userRole: string;
  onSave: (updated: TimeEntry) => void;
  onClose: () => void;
}

function toDatetimeLocalValue(isoTimestamp: string): string {
  const d = new Date(isoTimestamp);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function fromDatetimeLocalValue(localValue: string): string {
  return new Date(localValue).toISOString();
}

export default function EditEntryModal({ entry, userRole, onSave, onClose }: EditEntryModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    const first = dialogRef.current?.querySelector<HTMLElement>('select, input, button');
    first?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [category, setCategory] = useState(entry.category);
  const [position, setPosition] = useState(entry.position ?? '');
  const [apparatus, setApparatus] = useState(entry.apparatus ?? '');
  const [clockIn, setClockIn] = useState(toDatetimeLocalValue(entry.clock_in));
  const [clockOut, setClockOut] = useState(entry.clock_out ? toDatetimeLocalValue(entry.clock_out) : '');
  const [notes, setNotes] = useState(entry.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = getRolePriority(userRole) >= 50;

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    const payload: Record<string, unknown> = {};
    if (category !== entry.category) payload.category = category;
    if (position !== (entry.position ?? '')) payload.position = position || null;
    if (apparatus !== (entry.apparatus ?? '')) payload.apparatus = apparatus || null;
    if (notes !== (entry.notes ?? '')) payload.notes = notes;
    const newClockIn = fromDatetimeLocalValue(clockIn);
    if (newClockIn !== entry.clock_in) payload.clock_in = newClockIn;
    if (entry.clock_out) {
      const newClockOut = clockOut ? fromDatetimeLocalValue(clockOut) : undefined;
      if (newClockOut && newClockOut !== entry.clock_out) payload.clock_out = newClockOut;
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    try {
      const url = isAdmin ? `/api/admin/punch/${entry.id}` : `/api/punch/${entry.id}`;
      const updated = await apiFetch<TimeEntry>(url, { method: 'PUT', body: JSON.stringify(payload) });
      onSave(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    'w-full px-2.5 py-2 text-sm bg-surface-base border border-edge-default rounded-md text-content-primary outline-none box-border';
  const labelCls = 'block text-xs text-content-tertiary uppercase tracking-wider font-semibold mb-1';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit time entry"
      className="fixed inset-0 flex items-center justify-center bg-black/70 z-[1000] p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-[400px] max-h-[90vh] overflow-auto bg-surface-raised rounded-2xl border border-edge-default p-6 shadow-2xl"
      >
        <h2 className="text-lg font-bold text-action-primary mb-5 text-center">Edit Entry</h2>

        {error && (
          <div className="px-3.5 py-2.5 bg-red-500/15 border border-red-500/30 rounded-md text-red-300 text-[13px] mb-4">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3.5">
          <div>
            <label htmlFor="edit-category" className={labelCls}>
              Category
            </label>
            <select
              id="edit-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={inputCls}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {category === 'operational' && (
            <div>
              <label htmlFor="edit-position" className={labelCls}>
                Position
              </label>
              <select
                id="edit-position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className={inputCls}
              >
                <option value="">Select position</option>
                {POSITIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {category === 'operational' && (
            <div>
              <label htmlFor="edit-apparatus" className={labelCls}>
                Apparatus
              </label>
              <select
                id="edit-apparatus"
                value={apparatus}
                onChange={(e) => setApparatus(e.target.value)}
                className={inputCls}
              >
                <option value="">Select apparatus</option>
                {APPARATUS.filter((a) => !isFlexUnit(a.id)).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="edit-clock-in" className={labelCls}>
              Clock In
            </label>
            <input
              id="edit-clock-in"
              type="datetime-local"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              className={inputCls}
            />
          </div>

          {entry.clock_out && (
            <div>
              <label htmlFor="edit-clock-out" className={labelCls}>
                Clock Out
              </label>
              <input
                id="edit-clock-out"
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                className={inputCls}
              />
            </div>
          )}

          <div>
            <label htmlFor="edit-notes" className={labelCls}>
              Notes
            </label>
            <textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
              className={`${inputCls} resize-y`}
            />
            <div className="text-[11px] text-content-tertiary mt-0.5">{notes.length}/500</div>
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-3.5 text-base font-semibold rounded-lg border border-edge-default bg-transparent text-content-secondary cursor-pointer disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3.5 text-base font-bold rounded-lg border-none bg-action-primary text-content-on-primary cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
